import type { CSSProperties } from "react";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { VStack } from "../../primitives/stack";
import type { ModalProps } from "./types";
import { DefaultModalHeader } from "./Header/header";
import { DefaultModalFooter } from "./Footer/footer";
import { useStyles } from "./styles";

let bodyScrollLockCount = 0;
let savedBodyOverflow = "";

function acquireBodyScrollLock(): () => void {
  if (typeof document === "undefined") {
    return () => {};
  }
  if (bodyScrollLockCount === 0) {
    savedBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  bodyScrollLockCount += 1;
  return () => {
    bodyScrollLockCount -= 1;
    if (bodyScrollLockCount === 0) {
      document.body.style.overflow = savedBodyOverflow;
    }
  };
}

export function Modal({
  overLayColor,
  modalPosition,
  modalTop,
  modalLeft,
  justifyContent,
  alignItems,
  width = "50%",
  height = "50%",
  isModalOpen,
  onClose,
  styles: stylesProp,
  overlayClassName,
  modalClassName,
  bodyClassName,
  showHeader = true,
  customHeader,
  headerConfig,
  body,
  showFooter = true,
  customFooter,
  footerConfig,
}: ModalProps) {
  const styles = useStyles(
    overLayColor,
    width,
    height,
    modalPosition,
    modalTop,
    modalLeft,
    justifyContent,
    alignItems,
  );

  useEffect(() => {
    if (!isModalOpen) return;
    return acquireBodyScrollLock();
  }, [isModalOpen]);

  if (!isModalOpen) return null;
  if (!body && !showHeader && !showFooter) return null;

  const header = showHeader ? (
    customHeader ? (
      customHeader
    ) : headerConfig ? (
      <DefaultModalHeader {...headerConfig} />
    ) : null
  ) : null;

  const footer = showFooter ? (
    customFooter ? (
      customFooter
    ) : footerConfig ? (
      <DefaultModalFooter {...footerConfig} />
    ) : null
  ) : null;

  const modalContent = (
    <VStack
      className={overlayClassName}
      style={{ ...styles.overlay, ...stylesProp?.overlay }}
      onClick={onClose}
    >
      <VStack
        className={modalClassName}
        width={width}
        height={height}
        style={{ ...styles.modal, ...stylesProp?.modal }}
        onClick={(e) => e.stopPropagation()}
      >
        {header}

        {body && (
          <VStack
            className={bodyClassName}
            style={{
              ...styles.body,
              ...(stylesProp?.body as CSSProperties),
            }}
          >
            {body}
          </VStack>
        )}

        {footer}
      </VStack>
    </VStack>
  );

  if (typeof document !== "undefined") {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
}
